import { compactNumber } from "@/lib/formatNumber";
import { stripRichMarkers } from "@/lib/richFormat";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { avatarSrc } from "@/lib/defaults";
import { resolveMediaUrl } from "@/components/FileUpload";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { denominationLabel } from "@/lib/denominations";
import { Search, Users, Church, FileText, X } from "lucide-react";
import { RecentSearches } from "@/components/search/RecentSearches";
import { getRecent, addRecentQuery, addRecentEntity, removeRecent, clearRecent, recentPath } from "@/lib/recentSearches";

const Section = ({ title, children }) => (
  <div>
    <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yalola-text-muted)" }}>{title}</div>
    {children}
  </div>
);

const Row = ({ onClick, children, testId }) => (
  <button onClick={onClick} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[color:var(--yalola-surface-alt)] transition-colors" data-testid={testId}>
    {children}
  </button>
);

export const SearchBar = () => {
  const nav = useNavigate();
  const { t, user } = useApp();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);       // desktop inline dropdown
  const [expanded, setExpanded] = useState(false); // mobile/tablet icon → overlay
  const [recent, setRecent] = useState(() => getRecent(user?.id));
  const boxRef = useRef(null);
  const mobileRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => { setRecent(getRecent(user?.id)); }, [user?.id]);

  useEffect(() => {
    const onClickOut = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
      if (mobileRef.current && !mobileRef.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener("mousedown", onClickOut);
    return () => document.removeEventListener("mousedown", onClickOut);
  }, []);

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timerRef.current);
    if (v.trim().length < 2) { setResults(null); setOpen(true); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: v.trim() } });
        setResults(data);
        setOpen(true);
      } catch { /* noop */ }
    }, 300);
  };

  const go = (path) => { setOpen(false); setExpanded(false); setQ(""); setResults(null); nav(path); };
  // Tapped results are remembered so the member / church / group is one tap away next time.
  const goEntity = (path, entity) => { setRecent(addRecentEntity(user?.id, entity)); go(path); };
  // Full results page (Facebook-style): Enter key or the "See all results" row.
  const goSearchPage = (text = q) => {
    const v = (text || "").trim();
    if (v.length < 2) return;
    setRecent(addRecentQuery(user?.id, v));
    go(`/search?q=${encodeURIComponent(v)}`);
  };
  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); clearTimeout(timerRef.current); goSearchPage(); } };
  const pickRecent = (e) => (e.kind === "query" ? goSearchPage(e.q) : goEntity(recentPath(e), e));
  const removeRecentItem = (k) => setRecent(removeRecent(user?.id, k));
  const clearRecentItems = () => setRecent(clearRecent(user?.id));

  const showRecent = q.trim().length < 2 && recent.length > 0;
  const empty = results && !results.users?.length && !results.groups?.length && !results.churches?.length && !results.posts?.length;

  const seeAllRow = (testId) => (
    <button type="button" onClick={() => goSearchPage()} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm font-semibold border-t hover:bg-[color:var(--yalola-surface-alt)] transition-colors" style={{ color: "var(--yalola-primary)", borderColor: "var(--yalola-border)" }} data-testid={testId}>
      <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)" }}><Search size={15} /></span>
      <span className="truncate">{t("search.see_all_for", "Voir tous les résultats pour « {q} »").replace("{q}", q.trim())}</span>
    </button>
  );

  const renderResults = (seeAllTestId) => (
    <>
      {showRecent && <RecentSearches items={recent} onPick={pickRecent} onRemove={removeRecentItem} onClear={clearRecentItems} compact />}
      {empty && <div className="p-4 text-sm text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="search-empty">{t("search.no_results", "Aucun résultat pour « {q} »").replace("{q}", q)}</div>}
      {results?.users?.length > 0 && (
        <Section title={t("search.sec_members", "Membres")}>
          {results.users.map((u) => (
            <Row key={u.id} onClick={() => goEntity(`/profile/${u.id}`, { kind: "user", id: u.id, name: u.name, img: avatarSrc(u), sub: u.city })} testId={`search-user-${u.id}`}>
              <Avatar className="h-8 w-8"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-1">{u.name} <VerifiedBadge user={u} size={12} /></div>
                {u.city && <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{u.city}</div>}
              </div>
            </Row>
          ))}
        </Section>
      )}
      {results?.groups?.length > 0 && (
        <Section title={t("search.sec_groups", "Groupes")}>
          {results.groups.map((g) => (
            <Row key={g.id} onClick={() => goEntity(`/groups/${g.id}`, { kind: "group", id: g.id, name: g.name, img: g.cover_url, sub: t("search.members_count", "{n} membre(s)").replace("{n}", compactNumber(g.member_count)) })} testId={`search-group-${g.id}`}>
              {g.cover_url ? (
                <img src={resolveMediaUrl(g.cover_url)} alt="" className="h-8 w-8 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--yalola-secondary-soft)", color: "var(--yalola-secondary)" }}><Users size={15} /></div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{g.name}</div>
                <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("search.members_count", "{n} membre(s)").replace("{n}", compactNumber(g.member_count))}</div>
              </div>
            </Row>
          ))}
        </Section>
      )}
      {results?.churches?.length > 0 && (
        <Section title={t("search.sec_churches", "Églises")}>
          {results.churches.map((c) => (
            <Row key={c.id} onClick={() => goEntity(`/churches/${c.id}`, { kind: "church", id: c.id, name: c.name, img: c.logo_url, sub: c.city })} testId={`search-church-${c.id}`}>
              <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--yalola-accent-soft)", color: "var(--yalola-accent)" }}><Church size={15} /></div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{c.name}</div>
                <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{c.city}{c.denomination ? ` · ${denominationLabel(t, c.denomination)}` : ""}</div>
              </div>
            </Row>
          ))}
        </Section>
      )}
      {results?.posts?.length > 0 && (
        <Section title={t("search.sec_posts", "Publications")}>
          {results.posts.map((p) => (
            <Row key={p.id} onClick={() => go(`/post/${p.id}`)} testId={`search-post-${p.id}`}>
              <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><FileText size={15} /></div>
              <div className="min-w-0">
                <div className="text-sm truncate">{stripRichMarkers(p.content)}</div>
                <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("search.by", "par")} {p.author?.name}</div>
              </div>
            </Row>
          ))}
        </Section>
      )}
      {results && seeAllRow(seeAllTestId)}
    </>
  );

  return (
    <>
      {/* Desktop (lg+): inline input, fixed compact width so it never overlaps the centered nav */}
      <div className="hidden lg:block relative w-56 xl:w-64" ref={boxRef}>
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" style={{ color: "var(--yalola-text-muted)" }} />
        <Input
          placeholder={t("search.placeholder", "Rechercher sur Yalola")}
          value={q}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          className="pl-9 rounded-full h-10 bg-[color:var(--yalola-surface-alt)] border-0 focus-visible:ring-2"
          enterKeyHint="search" data-testid="global-search"
        />
        {open && (results || showRecent) && (
          <div className="absolute top-full mt-2 left-0 right-0 rounded-2xl border shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto z-50" style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }} data-testid="search-results">
            {renderResults("search-see-all")}
          </div>
        )}
      </div>

      {/* Mobile & tablet (< lg): icon only — expands to a full-width overlay to avoid overlapping the nav */}
      <div className="lg:hidden relative shrink-0" ref={mobileRef}>
        <button
          type="button"
          onClick={() => { setExpanded((v) => !v); }}
          className="h-10 w-10 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] transition-colors"
          style={{ color: "var(--yalola-text)" }}
          aria-label={t("search.placeholder", "Rechercher sur Yalola")}
          data-testid="search-icon-btn"
        >
          <Search size={20} />
        </button>
        {expanded && (
          <div className="fixed left-0 right-0 top-14 md:top-16 px-3 z-50" data-testid="search-overlay">
            <div className="max-w-[1400px] mx-auto relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" style={{ color: "var(--yalola-text-muted)" }} />
              <Input
                autoFocus
                placeholder={t("search.placeholder", "Rechercher sur Yalola")}
                value={q}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                className="pl-9 pr-10 rounded-full h-11 shadow-lg border focus-visible:ring-2"
                style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}
                enterKeyHint="search" data-testid="global-search-mobile"
              />
              <button type="button" onClick={() => { setExpanded(false); setQ(""); setResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--yalola-text-muted)]" data-testid="search-close-btn"><X size={18} /></button>
              {(results || showRecent) && (
                <div className="absolute top-full mt-2 left-0 right-0 rounded-2xl border shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto z-50" style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }} data-testid="search-results-mobile">
                  {renderResults("search-see-all-mobile")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
